import { copy, formatCopy, language, renderCommunityReportCount, state } from "./runtime.js";
import {
  filterRecordsForReportMapTimeWindow,
  formatReportMapRange,
} from "../../map-time-window.js";
import { ZCTA_POPULATION } from "../../zcta-population.js";
import { ZIP_COUNTY } from "../../zip-county.js";
import { COUNTY_POPULATION, MAP_LAB_COLOR_MODES, MAP_LAB_DATASET_URL, MAP_LAB_MAGNITUDE_METRICS, MAP_LAB_MIN_POPULATION, MAP_LAB_SHADE_LEVELS } from "./constants.js";
import { updateReportMapSources } from "./index.js";
import { robustShadeMax, shadeNorm, sickBand, sickRateQuantiles } from "./shading.js";
import { peopleRecordMatchesSymptoms } from "./symptom-filter.js";

export { peopleRecordMatchesSymptoms } from "./symptom-filter.js";

export async function loadCommunityReportCount() {
  // PREVIEW: render the static Map Lab dataset instead of the live current-week
  // count, so the generated data can be confirmed on the map. Revert this
  // function body to the /community-reports/count fetch to restore live data.
  try {
    const response = await fetch(MAP_LAB_DATASET_URL);
    if (!response.ok) return;
    const data = await response.json();
    const records = Array.isArray(data.records) ? data.records : [];
    state.mapLabRecords = records;
    return recomputeMapLabDerived();
  } catch {
    state.mapLabRecords = [];
    state.mapLabFilteredRecords = [];
    state.communityTotalReportCount = null;
    state.communityReportSummary = null;
    state.mapLabPoints = [];
    state.mapLabCategoryByZip = new Map();
    state.mapLabCategoryTotals = { total: 0, people: 0, animals: 0, environment: 0, zipCount: 0 };
    renderCommunityReportCount();
    return updateReportMapSources();
  }
}

// PREVIEW: re-summarize records in the active date window, then repaint.

export function recomputeMapLabDerived() {
  const records = Array.isArray(state.mapLabRecords) ? state.mapLabRecords : [];
  const timeFiltered = filterRecordsForReportMapTimeWindow(records, reportMapTimeWindow());
  // When a symptom filter is active it narrows PEOPLE reports to sick ones carrying ALL the
  // selected symptoms (healthy reports have no symptoms, so they drop out) — animals/environment
  // are untouched. With nothing selected this is exactly the time-filtered set.
  const symptoms = mapLabActiveSymptoms();
  const filteredRecords = symptoms.length
    ? timeFiltered.filter((record) => record.category !== "people" || peopleRecordMatchesSymptoms(record, symptoms))
    : timeFiltered;
  const summary = summarizeMapLabPeople(filteredRecords);
  state.mapLabFilteredRecords = filteredRecords;
  state.communityTotalReportCount = summary.total_count;
  state.communityReportSummary = summary;
  state.mapLabPoints = filteredRecords.filter(
    (record) => record.category === "animals" || record.category === "environment"
  );
  state.mapLabCategoryByZip = mapLabCategoryCountsByZip(filteredRecords);
  state.mapLabCategoryTotals = summarizeMapLabCategories(filteredRecords);
  renderCommunityReportCount();
  return updateReportMapSources();
}

// Outbreak symptom filter: [] = all reports; otherwise the checked symptom labels. A people
// record matches only when it is a sick report whose symptoms include EVERY selection
// (case-insensitive, so the menu labels and stored tokens don't have to match case).

export function mapLabActiveSymptoms() {
  const symptoms = state.mapLabSymptoms;
  if (!Array.isArray(symptoms)) return [];
  return symptoms.filter((symptom) => typeof symptom === "string" && symptom);
}

export function reportMapTimeWindow() {
  return {
    startDate: state.mapTimeStartDate,
    selectedDate: state.mapTimeSelectedDate,
  };
}


export function reportMapTimePeriodText() {
  return formatCopy("map_period_from", {
    range: formatReportMapRange(
      state.mapTimeStartDate,
      state.mapTimeSelectedDate,
      language(),
      copy("map_period_through")
    ),
  });
}

// PREVIEW: pick the more recent of two ISO timestamps, tolerating empty/invalid
// values so ZIPs missing a submitted_at still aggregate. Drives the "Latest report"
// line in the ZIP dot popup.
function laterTimestamp(current, candidate) {
  if (!candidate) return current || "";
  if (!current) return candidate;
  const currentTime = Date.parse(current);
  const candidateTime = Date.parse(candidate);
  if (Number.isNaN(candidateTime)) return current;
  if (Number.isNaN(currentTime)) return candidate;
  return candidateTime > currentTime ? candidate : current;
}

// PREVIEW helper: aggregate the Map Lab people records into the same summary
// shape the live count endpoint returns, so existing choropleth/dot rendering
// works unchanged.

export function summarizeMapLabPeople(records, limit = Infinity) {
  const byZip = new Map();
  let sickTotal = 0;
  let total = 0;
  let peopleSickTotal = 0;
  records.forEach((record) => {
    if (record.category !== "people") return;
    if (Number.isFinite(limit) && !(record.rank < limit)) return;
    const zip = String(record.zip_code || "");
    if (!/^\d{5}$/.test(zip)) return;
    const counts = byZip.get(zip) || {
      zip_code: zip,
      state: "Arizona",
      state_code: "AZ",
      sick_count: 0,
      healthy_count: 0,
      total_count: 0,
      people_sick_count: 0,
      weight: 0,
      latest_submitted_at: "",
    };
    if (record.status === "sick") {
      const peopleSick = Number.parseInt(record.people_sick_count, 10) || 0;
      counts.sick_count += 1;
      counts.people_sick_count += peopleSick;
      counts.weight += 1;
      sickTotal += 1;
      peopleSickTotal += peopleSick;
    } else {
      counts.healthy_count += 1;
    }
    counts.total_count += 1;
    counts.latest_submitted_at = laterTimestamp(counts.latest_submitted_at, record.submitted_at);
    total += 1;
    byZip.set(zip, counts);
  });
  const zipCounts = [...byZip.values()].sort((a, b) => a.zip_code.localeCompare(b.zip_code));
  return {
    count: sickTotal,
    sick_count: sickTotal,
    total_count: total,
    people_sick_count: peopleSickTotal,
    region: "Arizona ZIP codes (Map Lab preview)",
    period: "map_lab_preview",
    zip_counts: zipCounts,
    county_counts: [],
  };
}

// PREVIEW: aggregate animal + environment records per ZIP, bucketed by severity,
// for the cross-domain line in the ZIP polygon popup. Uses every record, so these
// counts are independent of the selected time-range estimate.

export function mapLabCategoryCountsByZip(records = state.mapLabRecords || []) {
  const byZip = new Map();
  records.forEach((record) => {
    if (record.category !== "animals" && record.category !== "environment") return;
    const zip = String(record.zip_code || "");
    if (!/^\d{5}$/.test(zip)) return;
    const detail = byZip.get(zip) || emptyZipCategoryDetail();
    const bucket = detail[record.category];
    bucket.count += 1;
    const severity = String(record.severity || "").toLowerCase();
    if (severity === "high" || severity === "medium" || severity === "low") {
      bucket[severity] += 1;
    }
    byZip.set(zip, detail);
  });
  return byZip;
}


export function emptyZipCategoryDetail() {
  return {
    animals: { count: 0, high: 0, medium: 0, low: 0 },
    environment: { count: 0, high: 0, medium: 0, low: 0 },
  };
}


export function summarizeMapLabCategories(records) {
  const totals = { total: 0, people: 0, animals: 0, environment: 0, zipCount: 0 };
  const zips = new Set();
  records.forEach((record) => {
    const category = record.category;
    if (category !== "people" && category !== "animals" && category !== "environment") return;
    totals.total += 1;
    totals[category] += 1;
    const zip = String(record.zip_code || "");
    if (/^\d{5}$/.test(zip)) zips.add(zip);
  });
  totals.zipCount = zips.size;
  return totals;
}

// PREVIEW: one ZIP's animal/environment aggregate (zeros when none), used by the
// ZIP polygon popup's cross-domain line.

export function mapLabZipDetail(zip) {
  const byZip = state.mapLabCategoryByZip;
  const detail = byZip instanceof Map ? byZip.get(String(zip)) : null;
  return detail || emptyZipCategoryDetail();
}

// PREVIEW helper: per-category layer visibility (people/animals/environment).

export function mapLabVisibility() {
  if (!state.mapLabVisible) {
    state.mapLabVisible = { people: true, animals: true, environment: true };
  }
  return state.mapLabVisible;
}

// PREVIEW helper: active ZIP-shading magnitude metric (defaults to total reports).

export function mapLabMagnitudeMetric() {
  return MAP_LAB_MAGNITUDE_METRICS.includes(state.mapLabMagnitudeMetric)
    ? state.mapLabMagnitudeMetric
    : "reports";
}

// People are always represented by outbreak dots on the homepage; the category toggle
// controls their visibility.

export function mapLabDotsVisible() {
  return true;
}

// PREVIEW helper: active shading geography level (defaults to the county overview).

export function mapLabShadeLevel() {
  return MAP_LAB_SHADE_LEVELS.includes(state.mapLabShadeLevel) ? state.mapLabShadeLevel : "county";
}

// PREVIEW helper: active color-scale mode (defaults to the smooth gradient).

export function mapLabColorMode() {
  return MAP_LAB_COLOR_MODES.includes(state.mapLabColorMode) ? state.mapLabColorMode : "smooth";
}

// PREVIEW helper: the county currently drilled into (ZIP view filtered to it), or "".

export function mapLabDrillCounty() {
  return state.mapLabDrillCounty || "";
}

// PREVIEW: yellow→orange→red sequential ramp on a sick-rate expression — higher =
// sicker. Uses the ColorBrewer YlOrRd family (#fecc5c / #fd8d3c / #bd0026). The domain
// is TIGHTENED to the real per-ZIP spread (~0.43–0.73) instead of 0–1, so small
// differences between ZIPs use the whole color range.

export function mapLabPointLimit() {
  return Infinity;
}

// PREVIEW: one category's true lat/long points as a GeoJSON FeatureCollection,
// capped at the active range's estimated animal/environment sample size.

export function mapLabCategoryGeoJson(category) {
  const points = Array.isArray(state.mapLabPoints) ? state.mapLabPoints : [];
  return {
    type: "FeatureCollection",
    features: points
      .filter((point) => point.category === category && Number.isFinite(point.lng) && Number.isFinite(point.lat))
      .slice(0, mapLabPointLimit())
      .map((point) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [point.lng, point.lat] },
        properties: {
          category,
          id: point.id || "",
          subtype: point.subtype || "",
          severity: point.severity || "",
          zip_code: point.zip_code || "",
          submitted_at: point.submitted_at || "",
        },
      })),
  };
}


// PREVIEW helper: show a pin category's clustered OR flat layers (never both), and only
// when the category is on. One `map` variant for fresh-map init; the plural fans out.

export function joinZipCounts(geojson, zipCounts = reportCountsByZip()) {
  const countsByZip = new Map(zipCounts.map((zipCount) => [zipCount.zip_code, zipCount]));
  // First pass: derive each ZIP's counts/rate so the relative opacity scale can be built
  // across the whole set before stamping per-feature norms in the second pass.
  const rows = (geojson.features || []).map((feature) => {
    const zipCode = feature.properties?.GEOID || feature.properties?.ZCTA5;
    const counts = countsByZip.get(zipCode) || {};
    const sickCount = Number.isInteger(counts.sick_count) ? counts.sick_count : 0;
    const totalCount = Number.isInteger(counts.total_count) ? counts.total_count : 0;
    const population = Number.isFinite(ZCTA_POPULATION[zipCode]) ? ZCTA_POPULATION[zipCode] : 0;
    // health_score: sick share, null when there are no reports (grayed on the map).
    const healthScore = totalCount > 0 ? sickCount / totalCount : null;
    // rate: reports per capita, null when population is unknown/zero (divide-by-zero guard).
    const rate = population > 0 ? totalCount / population : null;
    // Only ZIPs above the population floor are shaded in rate mode, so only they feed the
    // rate scale (matches zipSuppressionExpression()).
    const rateForScale = population >= MAP_LAB_MIN_POPULATION && rate != null ? rate : 0;
    return { feature, zipCode, counts, sickCount, totalCount, population, healthScore, rate, rateForScale };
  });
  const reportsMax = robustShadeMax(rows.map((row) => row.totalCount));
  const rateMax = robustShadeMax(rows.map((row) => row.rateForScale));
  // Sick-rate percentile cut points across these ZIPs, driving the "3 bands" color mode.
  const sickQuantiles = sickRateQuantiles(rows);
  return {
    ...geojson,
    features: rows.map((row) => ({
      ...row.feature,
      properties: {
        ...row.feature.properties,
        // county stamped for the drill-down filter (ZIP view scoped to one county).
        county: ZIP_COUNTY[row.zipCode] || "",
        // sick_band: 0/1/2 (yellow/orange/red) for the banded color mode; stamped here so
        // flipping color modes only repaints (no re-join). Grayed areas ignore it.
        sick_band: sickBand(row.healthScore, sickQuantiles),
        sick_count: row.sickCount,
        healthy_count: Number.isInteger(row.counts.healthy_count) ? row.counts.healthy_count : 0,
        total_count: row.totalCount,
        people_sick_count: Number.isInteger(row.counts.people_sick_count) ? row.counts.people_sick_count : 0,
        // latest_submitted_at flows into the ZIP dot popup's "Latest report" line.
        latest_submitted_at: typeof row.counts.latest_submitted_at === "string" ? row.counts.latest_submitted_at : "",
        population: row.population,
        health_score: row.healthScore,
        // sick_rate stays numeric for the color ramp; no-data ZIPs are grayed by the
        // dynamic feature styling step, not this value.
        sick_rate: row.healthScore == null ? 0 : row.healthScore,
        rate: row.rate == null ? 0 : row.rate,
        // reports_norm / rate_norm: 0..1 magnitude relative to peers, driving fill opacity.
        reports_norm: shadeNorm(row.totalCount, reportsMax),
        rate_norm: shadeNorm(row.rateForScale, rateMax),
      },
    })),
  };
}

// PREVIEW (Map Lab county shading): roll the per-ZIP counts up to counties via the
// ZIP_COUNTY crosswalk, SUMMING raw ingredients (sick/total) — never averaging rates.

export function countyCountsByName() {
  const byCounty = new Map();
  reportCountsByZip().forEach((zipCount) => {
    const county = ZIP_COUNTY[String(zipCount.zip_code || "")];
    if (!county) return;
    const bucket = byCounty.get(county) || {
      sick_count: 0,
      healthy_count: 0,
      total_count: 0,
      people_sick_count: 0,
    };
    const sick = Number.isInteger(zipCount.sick_count) ? zipCount.sick_count : 0;
    const total = Number.isInteger(zipCount.total_count) ? zipCount.total_count : 0;
    bucket.sick_count += sick;
    bucket.healthy_count += Number.isInteger(zipCount.healthy_count)
      ? zipCount.healthy_count
      : Math.max(0, total - sick);
    bucket.total_count += total;
    bucket.people_sick_count += Number.isInteger(zipCount.people_sick_count) ? zipCount.people_sick_count : 0;
    byCounty.set(county, bucket);
  });
  return byCounty;
}

// PREVIEW (Map Lab county shading): attach the summed county counts + derived
// health/rate to each county feature, using the SAME property names as joinZipCounts()
// so the shading paint expressions work unchanged.

export function joinCountyCounts(geojson) {
  const countsByCounty = countyCountsByName();
  // First pass mirrors joinZipCounts(): counties normalize among counties (not ZIPs), so
  // the relative opacity scale is built from the county set here.
  const rows = (geojson.features || []).map((feature) => {
    const name = feature.properties?.NAME || "";
    const counts = countsByCounty.get(name) || {};
    const sickCount = Number.isInteger(counts.sick_count) ? counts.sick_count : 0;
    const totalCount = Number.isInteger(counts.total_count) ? counts.total_count : 0;
    const population = Number.isFinite(COUNTY_POPULATION[name]) ? COUNTY_POPULATION[name] : 0;
    const healthScore = totalCount > 0 ? sickCount / totalCount : null;
    const rate = population > 0 ? totalCount / population : null;
    const rateForScale = population >= MAP_LAB_MIN_POPULATION && rate != null ? rate : 0;
    return { feature, name, counts, sickCount, totalCount, population, healthScore, rate, rateForScale };
  });
  const reportsMax = robustShadeMax(rows.map((row) => row.totalCount));
  const rateMax = robustShadeMax(rows.map((row) => row.rateForScale));
  // Sick-rate percentile cut points across counties (their own spread, not the ZIPs').
  const sickQuantiles = sickRateQuantiles(rows);
  return {
    ...geojson,
    features: rows.map((row) => ({
      ...row.feature,
      properties: {
        ...row.feature.properties,
        county_name: row.name,
        sick_band: sickBand(row.healthScore, sickQuantiles),
        sick_count: row.sickCount,
        healthy_count: Number.isInteger(row.counts.healthy_count) ? row.counts.healthy_count : 0,
        total_count: row.totalCount,
        people_sick_count: Number.isInteger(row.counts.people_sick_count) ? row.counts.people_sick_count : 0,
        population: row.population,
        health_score: row.healthScore,
        sick_rate: row.healthScore == null ? 0 : row.healthScore,
        rate: row.rate == null ? 0 : row.rate,
        reports_norm: shadeNorm(row.totalCount, reportsMax),
        rate_norm: shadeNorm(row.rateForScale, rateMax),
      },
    })),
  };
}


export function reportCountsByZip() {
  const liveZipCounts = state.communityReportSummary?.zip_counts;
  if (Array.isArray(liveZipCounts) && liveZipCounts.length > 0) {
    return liveZipCounts.map(normalizeZipCount);
  }
  return localReportCountsByZip();
}


export function normalizeZipCount(zipCount) {
  const sickCount = Number.isInteger(zipCount.sick_count) ? zipCount.sick_count : 0;
  const healthyCount = Number.isInteger(zipCount.healthy_count)
    ? zipCount.healthy_count
    : Math.max(0, (zipCount.total_count || 0) - sickCount);
  return {
    zip_code: String(zipCount.zip_code || ""),
    sick_count: sickCount,
    healthy_count: healthyCount,
    total_count: Number.isInteger(zipCount.total_count) ? zipCount.total_count : sickCount + healthyCount,
    people_sick_count: Number.isInteger(zipCount.people_sick_count) ? zipCount.people_sick_count : 0,
    weight: Number.isInteger(zipCount.weight) ? zipCount.weight : sickCount,
    latest_submitted_at: typeof zipCount.latest_submitted_at === "string" ? zipCount.latest_submitted_at : "",
  };
}


export function localReportCountsByZip() {
  const countsByZip = new Map();
  const reports = filterRecordsForReportMapTimeWindow(
    JSON.parse(localStorage.getItem("community-reports") || "[]"),
    reportMapTimeWindow()
  );
  reports.forEach((report) => {
    const zipCode = String(report.zip_code || "");
    if (!/^\d{5}$/.test(zipCode)) return;
    const counts = countsByZip.get(zipCode) || {
      zip_code: zipCode,
      sick_count: 0,
      healthy_count: 0,
      total_count: 0,
      people_sick_count: 0,
      weight: 0,
      latest_submitted_at: "",
    };
    if (report.feeling === "sick") {
      counts.sick_count += 1;
      counts.people_sick_count += Number.parseInt(report.people_sick_count, 10) || 0;
      counts.weight += 1;
    } else {
      counts.healthy_count += 1;
    }
    counts.total_count += 1;
    counts.latest_submitted_at = laterTimestamp(counts.latest_submitted_at, report.submitted_at);
    countsByZip.set(zipCode, counts);
  });
  return [...countsByZip.values()].sort((a, b) => a.zip_code.localeCompare(b.zip_code));
}

// PREVIEW: one aggregated marker per ZIP at its centroid (replaces per-person
// jittered dots). Sized by report volume, colored by sick share, click for a popup.
